import { FC } from 'react';
import type { UserCourse } from '@teachur/http-client';
import { Checkbox } from '@new/common/ui/Checkbox/Checkbox';
import { cn } from '@new/lib';
import { StatusBadge } from './StatusBadge';
import { CourseActionButtons } from './CourseActionButtons';

interface CourseListItemProps {
  userCourse: UserCourse;
  isSelected: boolean;
  isActive: boolean;
  isAdmin: boolean;
  onToggleSelect: (id: number) => void;
  onRowClick: (userCourse: UserCourse) => void;
  onStart: (userCourse: UserCourse) => void;
  onStop: (userCourse: UserCourse) => void;
  onDelete: (userCourse: UserCourse) => void;
}

export const CourseListItem: FC<CourseListItemProps> = ({
  userCourse,
  isSelected,
  isActive,
  isAdmin,
  onToggleSelect,
  onRowClick,
  onStart,
  onStop,
  onDelete,
}) => {
  const courseName = userCourse.course?.name ?? '—';
  const notesCount = userCourse.notes?.length ?? 0;

  return (
    <div
      onClick={() => onRowClick(userCourse)}
      className={cn(
        'flex cursor-pointer items-center justify-between gap-2 rounded-lg border-3 px-4 py-3 transition-colors',
        isActive
          ? 'border-(--primary-O600) bg-(--warning-W100)'
          : 'border-(--neutral-white-NW800) bg-(--neutral-white-NW1000) hover:border-(--primary-O400)'
      )}
    >
      <div className="flex min-w-0 items-center gap-4">
        {isAdmin && (
          <div
            className="flex shrink-0 origin-left scale-75 items-center"
            onClick={(event) => event.stopPropagation()}
          >
            <Checkbox
              variant="default"
              value={isSelected}
              onChange={() => onToggleSelect(userCourse.id)}
            />
          </div>
        )}

        <div className="flex min-w-0 flex-col gap-1">
          <p className="[font-family:var(--font-montserrat)] [font-size:var(--fs-p4)] m-0! truncate font-medium leading-tight text-(--neutral-black-NB1000)">
            {courseName}
          </p>

          <p className="m-0! text-xs font-medium text-(--neutral-black-NB600)!">
            {notesCount} note{notesCount !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <StatusBadge userCourse={userCourse} showPassedDate />

        <CourseActionButtons
          userCourse={userCourse}
          isAdmin={isAdmin}
          onStart={onStart}
          onStop={onStop}
          onDelete={onDelete}
        />
      </div>
    </div>
  );
};
